import React, { useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { Icon, Button, sendCustomTrackingInfo } from '@panel/commons';
import { isZonaDemandSite } from '@panel/commons/helpers/siteUtils';
import { getMainColor, getNeutralColors } from '@panel/commons/lib/theme';
import { useTranslation } from 'react-i18next';
import { BodyText2Css } from '@panel/commons/lib/typography';
import { useThemeBreakpoint } from '@panel/leads/src/components/hooks/useResize';
import { Modal } from '@panel/avisos/src/components/Modals/ModalWrapper';
import { puerSendTrackingInfo } from '@panel/usuarios/src/services/utils/gtmUtils';
import { useDispatch, useSelector } from 'react-redux';
import SimpleTag from '@panel/commons/lib/SimpleTag';
import { createSvgComponent } from '@panel/commons/helpers/scoreHelpers';
import { userInfoOperations } from '../../../../state/ducks/user';
import { HeaderMenuItemList } from './headerMenuItemList/HeaderMenuItemList';
import { HeaderMenuFooter } from './headerMenuFooter/HeaderMenuFooter';
import MessageCounter from '../../messageActivityCounter';
import { BorderAvatar, ChrevonRigth } from '../../../../assets';
import { useModal } from '../../../../components/modal';
import PuerModalType from '../../../../components/modal/puerModalType';
import { usePublisherUnifiedPanel } from '../../../../hooks/usePublisherUnifiedPanel';
import { getLinkToPublish } from '../../../../utils/getPublisherLinks';
import { bumexFeatureSelectors } from '../../../../state/ducks/bumexFeatures';

const HeaderMenuWrapper = styled.div`
  background-color: ${getNeutralColors('white')};
  display: flex;
  flex-direction: column;
  height: 100%;
  opacity: 0;
  overflow: ${({ modal }) => (modal ? 'none' : 'auto')};
  padding-bottom: 48px;
  position: ${({ modal }) => (modal ? 'absolute' : 'fixed')};
  top: ${props => (props.isBrasil ? '90px' : '60px')};
  transition: opacity 0.2s ease 0s, transform 0.4s ease 0s, visibility 0.2s ease 0s;
  visibility: hidden;
  width: 100%;
  z-index: 99999;
  transform: translateX(100%);

  ${props =>
    props.isOpen &&
    css`
      opacity: 1;
      transform: translateX(0%);
      visibility: visible;
    `}
`;

const ButtonsWrapper = styled.div`
  display: inline-grid;
  padding: 0px 16px 56px;
`;

const ContentDropdown = styled.div`
  background-color: ${getNeutralColors('grisClaroIII')};
  display: block;
  overflow: hidden;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
`;

const DropdownWrapper = styled.div`
  background-color: ${getNeutralColors('white')};
  margin-bottom: 16px;
`;

const MenuDropdown = styled.div`
  border-bottom: 1px solid ${getNeutralColors('grisClaroII')};
  .headerDropdown > svg {
    position: absolute;
    right: 0;
  }
  .contentDropdown {
    display: block;
    overflow: hidden;
    margin-left: 15px;
  }
  &&.dropdown__close {
    ${ContentDropdown} {
      height: 0px;
    }
  }
  &&.dropdown__open {
    ${ContentDropdown} {
      height: 100%;
      border-top: 1px solid ${getNeutralColors('grisClaroII')};
    }
  }
`;

const HeaderDropdown = styled.div`
  overflow: hidden;
  padding: 16px;
  width: 100%;
  position: relative;
  display: flex;
  justify-content: space-between;
  .mainContainer {
    display: flex;
    gap: 16px;
  }
  .userNameContainer {
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
  }
  .userName {
    font-size: 18px;
    font-weight: 700;
    position: absolute;
    top: 10px;
  }
`;

const AvatarImg = styled.svg.attrs({
  version: '1.1',
  xmlns: 'http://www.w3.org/2000/svg',
  xmlnsXlink: 'http://www.w3.org/1999/xlink'
})`
  height: 40px;
  width: 40px;

  ${({ active }) =>
    active &&
    css`
      svg {
        .externalBorder {
          stroke: ${getMainColor()};
        }
      }
    `};
`;

const UserName = styled.p`
  max-width: 255px;
  color: ${getNeutralColors('black')};
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 16px;
  font-style: normal;
  font-weight: 600;
  line-height: 130%;
`;

const UserDataSub = styled.p`
  ${BodyText2Css};
  max-width: 255px;
  color: ${getNeutralColors('grisMedioII')};
  float: left;
  overflow: hidden;
  text-overflow: ellipsis;
  user-select: text;
  font-size: 16px;
  font-weight: 400;
  line-height: normal;
`;

const IconWrapper = styled.div`
  height: inherit;
  padding: 6px 0px 3px;
  display: flex;
  align-items: center;
`;

const HeaderText = styled.div`
  white-space: break-spaces;
  margin-bottom: 10px;
  color: ${getNeutralColors('grisOscuroAccesible')};
`;

const LevelDataContainer = styled.div`
  border-top: 1px solid ${getNeutralColors('grisClaroIII')};
  line-height: 0px;
  padding: 0px 0px 8px 20px;
  margin-bottom: 0px;
  display: flex;
  align-items: center; /* Alinea los elementos al centro verticalmente */
  justify-content: space-between; /* Distribuye espacio entre los elementos */
  cursor: pointer;
  height: 48px;
  background: ${getNeutralColors('grisClaroIII')};
  border-top: 1px solid ${getNeutralColors('grisClaroIII')};
  border-bottom: 1px solid ${getNeutralColors('grisClaroIII')};
`;

const DataContainer = styled.div`
  width: 250px;
  display: flex;
`;

const SpanLevel = styled.span`
  font-family: Hind;
  font-size: 16px;
  font-style: normal;
  font-weight: 600;
  padding-left: 8px;
  padding-top: 18px;
`;
const ScoreContainer = styled.div`
  padding-left: 8px;
  padding-top: 12px;
`;

const ChevronContainer = styled.div`
  margin-right: 8px;
  padding-top: 9px;
`;

function HeaderMenu({ user, isOpen, onMenuItemClick, isBrasil, isPortalWithNewReputation, reputationLevelState }) {
  const [isOpenUser, setIsOpenUser] = useState(true);
  const dispatch = useDispatch();
  const toggleAccordion = () => {
    setIsOpenUser(!isOpenUser);
  };
  const { bumexFeatures } = useSelector(bumexFeatureSelectors.getBumexFeatureState);
  const { t } = useTranslation();
  const history = useHistory();
  const { pathname } = useLocation();

  const { publisherTypeId, publisherId, userTypeId, isSeeker, isPublisherMasked, name, email } = user;
  const NameUser = user.name ? `${user.name} ${user.lastName || ''}` : user.email.split('@')[0];
  const siteId = process.env.REACT_APP_BUILD_SITE;
  const isBrasilSite = ['WIBR', 'IWBR', 'CMBR'].includes(siteId);
  const isLandingZD = window.location.href.includes('zona-demand') && isZonaDemandSite;
  const isSheredDesempeno = window.location.href.includes('compartir-desempeno');
  const withHeaderButtons = !history.location.pathname.includes('publicador');
  const { toggleModal, letToggleModal } = useModal();

  const [redirectPublisher, setRedirectPublisher] = useState(false);
  const [breakpoint] = useThemeBreakpoint();

  const sitesWithoutEcommerce = ['PLEC', 'LLVE'];
  const canBuyPlans = sitesWithoutEcommerce.indexOf(siteId) === -1;
  const userIsParticular =
    (publisherTypeId === '1' || isSeeker) &&
    ['DVPE', 'UBPE', 'ZPAR', '24MX', 'IWBR', 'WIBR', 'PLEC', 'CAPA', 'CMBR', 'VAMX'].includes(siteId);
  const buyPlansUrl = userIsParticular ? t('panel.url.ecommerce.particular') : t('panel.url.ecommerce.professional');
  const isAdmin = !isSeeker && userTypeId === '1' && publisherId.toLowerCase() !== 'null';

  const { getAutologin } = usePublisherUnifiedPanel();
  const nickNameUser = name ? name.toUpperCase().split('')[0] : email && email.toUpperCase().split('')[0];
  const SvgComponent = createSvgComponent(reputationLevelState.levelValue);

  const handleOnClicVerReputacion = () => {
    sendCustomTrackingInfo({
      event: 'select_content',
      content_type: 'Menu hamburguesa',
      item_id: 'Nivel'
    });
    if (!pathname.includes('estadisticas')) {
      window.open(`${window.location.origin}${t('panel.reputacion.url')}`);
    }
  };

  const handleClick = () => {
    if (isPublisherMasked) {
      letToggleModal(true);
      getAutologin({ modal: 'timer' });
    }
    window.dataLayer.push({
      event: 'trackEvent',
      eventCategory: 'Publicador',
      eventAction: 'Iniciar Publicacion',
      eventLabel: 'CTA Header Publicar gratis - Panel'
    });
  };

  const handleClickBuy = () => {
    puerSendTrackingInfo({
      event: 'trackEvent',
      eventCategory: 'Publicador',
      eventAction: 'Compra de planes',
      eventLabel: 'CTA Header Comprar'
    });
  };

  const handleLogin = () => {
    dispatch(userInfoOperations.showLogin(true));
    // window.location = `/login.bum?loginUrl=${window.location.href}`;
  };

  const getHeader = () => {
    if (user.isLoading && (isLandingZD || isSheredDesempeno)) return null;
    if (!user.email && (isLandingZD || isSheredDesempeno)) {
      return (
        <ButtonsWrapper>
          {isLandingZD && <HeaderText>{t('panel.header.login.text.mobile')}</HeaderText>}
          <Button
            buttonType='solidButton'
            dataPanel='headerMenuM_btnLogin_ZD'
            linkRef=''
            onClick={handleLogin}
            text={t('panel.header.title.login')}
            hrefDisabled
          />
        </ButtonsWrapper>
      );
    }
    return (
      <>
        <DropdownWrapper>
          <HeaderDropdown role='button' aria-hidden='true' onClick={toggleAccordion}>
            <div className='mainContainer' data-tour-panel='reputation-level'>
              {nickNameUser && (
                <div className='userNameContainer'>
                  <AvatarImg active={isOpenUser} site={siteId}>
                    <BorderAvatar width={40} height={40} />
                  </AvatarImg>
                  <div className='userName'>{nickNameUser}</div>
                </div>
              )}
              <div>
                <UserName site={siteId}>{NameUser}</UserName>
                <UserDataSub title={user.email}>
                  {isAdmin ? t('panel.configuration.data.publisherId', { value: user.publisherId }) : user.email}
                </UserDataSub>
              </div>
            </div>

            <IconWrapper data-tour-panel='reputation-levelChevron'>
              <Icon
                name={isOpenUser ? 'chevronUp' : 'chevronDown'}
                width={17}
                height={18}
                viewBox='0 0 15 18'
                colorFill='dark'
              />
            </IconWrapper>
            {!isOpen && <MessageCounter isMobile />}
          </HeaderDropdown>

          {isPortalWithNewReputation && user.publisherTypeId !== '1' && !user.isSeeker && (
            <LevelDataContainer onClick={handleOnClicVerReputacion} data-tour-panel='reputation-levelValue'>
              <DataContainer>
                <SpanLevel>
                  {reputationLevelState.levelValue
                    ? t(`panel.reputacion.scoring.level.${reputationLevelState.levelValue}`)
                    : t('panel.reputacion.scoring.level.0')}
                </SpanLevel>
                <ScoreContainer>
                  <SvgComponent />
                  {isBrasilSite && <SimpleTag text={t('panel.reputacion.badge.menu')} />}
                </ScoreContainer>

                <ChevronContainer>
                  <ChrevonRigth />
                </ChevronContainer>
              </DataContainer>
            </LevelDataContainer>
          )}

          <MenuDropdown className={isOpenUser ? 'dropdown__open' : 'dropdown__close'}>
            <ContentDropdown>
              <HeaderMenuItemList onMenuItemClick={onMenuItemClick} user={user} />
              <HeaderMenuFooter onMenuItemClick={onMenuItemClick} />
            </ContentDropdown>
          </MenuDropdown>
        </DropdownWrapper>
        {withHeaderButtons && (
          <ButtonsWrapper>
            <Button
              buttonType='solidButton'
              dataPanel='headerMenuM_btnPublish'
              onClick={handleClick}
              linkRef={getLinkToPublish(userIsParticular, bumexFeatures)}
              text={t('panel.header.btn.publish')}
              hrefDisabled={isPublisherMasked}
            />
            {canBuyPlans && !userIsParticular && (
              <Button
                buttonType='borderButton'
                dataPanel='headerDesktop_btnBuy'
                onClick={handleClickBuy}
                linkRef={buyPlansUrl}
                target='_blank'
                text={t('panel.header.btn.buy')}
              />
            )}
          </ButtonsWrapper>
        )}
      </>
    );
  };
  return (
    <HeaderMenuWrapper data-panel='headerMenuM' isBrasil={isBrasil} isOpen={isOpen} modal={toggleModal}>
      {getHeader()}
      {toggleModal && (
        <Modal>
          <PuerModalType
            breakpoint={breakpoint}
            redirectPublisher={redirectPublisher}
            setRedirectPublisher={setRedirectPublisher}
            siteId={siteId}
            isPublisherMasked={isPublisherMasked}
          />
        </Modal>
      )}
    </HeaderMenuWrapper>
  );
}

export { HeaderMenu };
